import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ecommerceService } from '../../services/ecommerceService';

// Async thunks
export const fetchComparison = createAsyncThunk(
  'ecommerceComparison/fetchComparison',
  async (_, { rejectWithValue }) => {
    try {
      const response = await ecommerceService.getComparison();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchComparisonMatrix = createAsyncThunk(
  'ecommerceComparison/fetchComparisonMatrix',
  async (_, { rejectWithValue }) => {
    try {
      const response = await ecommerceService.getComparisonMatrix();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const addToComparison = createAsyncThunk(
  'ecommerceComparison/addToComparison',
  async (data, { rejectWithValue }) => {
    try {
      const response = await ecommerceService.addToComparison(data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const removeFromComparison = createAsyncThunk(
  'ecommerceComparison/removeFromComparison',
  async (comparisonItemId, { rejectWithValue }) => {
    try {
      const response = await ecommerceService.removeFromComparison(comparisonItemId);
      return { comparisonItemId, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const clearComparison = createAsyncThunk(
  'ecommerceComparison/clearComparison',
  async (_, { rejectWithValue }) => {
    try {
      const response = await ecommerceService.clearComparison();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const addComparisonToCart = createAsyncThunk(
  'ecommerceComparison/addComparisonToCart',
  async ({ comparisonItemId, quantity = 1 }, { rejectWithValue }) => {
    try {
      const response = await ecommerceService.addComparisonToCart(comparisonItemId, quantity);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const mergeComparisonSession = createAsyncThunk(
  'ecommerceComparison/mergeSession',
  async (sessionKey, { rejectWithValue }) => {
    try {
      const response = await ecommerceService.mergeComparisonSession(sessionKey);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Slice
const ecommerceComparisonSlice = createSlice({
  name: 'ecommerceComparison',
  initialState: {
    comparisonList: null,
    items: [],
    matrix: null,
    loading: false,
    error: null,
    adding: false,
    removing: false,
    itemCount: 0,
    isFull: false,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch comparison
    builder
      .addCase(fetchComparison.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchComparison.fulfilled, (state, action) => {
        state.loading = false;
        state.comparisonList = action.payload;
        state.items = action.payload.items || [];
        state.itemCount = action.payload.item_count || 0;
        state.isFull = action.payload.is_full || false;
      })
      .addCase(fetchComparison.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Fetch comparison matrix
    builder
      .addCase(fetchComparisonMatrix.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchComparisonMatrix.fulfilled, (state, action) => {
        state.loading = false;
        state.matrix = action.payload;
      })
      .addCase(fetchComparisonMatrix.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Add to comparison
    builder
      .addCase(addToComparison.pending, (state) => {
        state.adding = true;
        state.error = null;
      })
      .addCase(addToComparison.fulfilled, (state, action) => {
        state.adding = false;
        if (action.payload.item) {
          state.items.push(action.payload.item);
          state.itemCount = action.payload.item_count || state.items.length;
          state.isFull = state.itemCount >= 4;
        }
      })
      .addCase(addToComparison.rejected, (state, action) => {
        state.adding = false;
        state.error = action.payload;
      });

    // Remove from comparison
    builder
      .addCase(removeFromComparison.pending, (state) => {
        state.removing = true;
        state.error = null;
      })
      .addCase(removeFromComparison.fulfilled, (state, action) => {
        state.removing = false;
        state.items = state.items.filter(
          (item) => item.id !== action.payload.comparisonItemId
        );
        state.itemCount = state.items.length;
        state.isFull = state.itemCount >= 4;
      })
      .addCase(removeFromComparison.rejected, (state, action) => {
        state.removing = false;
        state.error = action.payload;
      });

    // Clear comparison
    builder
      .addCase(clearComparison.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(clearComparison.fulfilled, (state) => {
        state.loading = false;
        state.items = [];
        state.itemCount = 0;
        state.isFull = false;
        state.matrix = null;
      })
      .addCase(clearComparison.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Add comparison to cart
    builder
      .addCase(addComparisonToCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addComparisonToCart.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(addComparisonToCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Merge session
    builder
      .addCase(mergeComparisonSession.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(mergeComparisonSession.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(mergeComparisonSession.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError } = ecommerceComparisonSlice.actions;
export default ecommerceComparisonSlice.reducer;
